import { createReducer, on } from '@ngrx/store';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { Logger } from '@app/common/logger';
import {
  PLAuthError,
  PLAuthPending,
  PLAuthSuccess,
  PLImpersonationStart,
  PLImpersonationStop,
} from './actions';
import { ImpersonationState, PLAuthState } from './state';

const logger = new Logger('PLAuth');

// Pure initial state for type safety
const initialState: PLAuthState = {
  authState: 'init',
  isSiriusEnabled: false,
  impersonation: {
    isActive: false,
    token: null,
    userData: null,
    targetUserId: null,
    expiresAt: null,
  },
};

// Helper to restore impersonation state from sessionStorage
function getImpersonationStateFromSession(): ImpersonationState {
  const token = sessionStorage.getItem('impersonation_token');
  const userDataStr = sessionStorage.getItem('impersonation_user_data');
  const metaStr = sessionStorage.getItem('impersonation_meta');

  if (!token) {
    return {
      isActive: false,
      token: null,
      userData: null,
      targetUserId: null,
      expiresAt: null,
    };
  }

  let userData = null;
  let targetUserId = null;
  let expiresAt = null;

  try {
    if (userDataStr) {
      userData = JSON.parse(userDataStr);
    }
  } catch (error) {
    // Ignore parse errors
  }

  try {
    if (metaStr) {
      const meta = JSON.parse(metaStr);
      targetUserId = meta.targetUserId || null;
      expiresAt = meta.expiresAt || null;
    }
  } catch (error) {
    // Ignore parse errors
  }

  return {
    isActive: true,
    token,
    userData,
    targetUserId,
    expiresAt,
  };
}

// Factory for initial state, reading from localStorage or using getIsSiriusEnabled
export function getInitialAuthState(): PLAuthState {
  const isSiriusEnabled = getIsSiriusEnabled();
  return {
    ...initialState,
    isSiriusEnabled,
    impersonation: getImpersonationStateFromSession(),
  };
}

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  return JSON.parse(jsonPayload);
}

export const reducer = createReducer(
  initialState,
  on(
    PLAuthPending,
    (state): PLAuthState => ({
      ...state,
      authState: 'pending',
    })
  ),
  on(PLAuthSuccess, (state, { user, token }): PLAuthState => {
    // Validate token before parsing
    if (!token) {
      logger.error('PLAuthSuccess received with undefined/empty token');
      return state; // Return unchanged state if no token
    }

    let jwt: any;
    try {
      jwt = parseJwt(token);
    } catch (error) {
      logger.error('Failed to parse JWT token');
      return state; // Return unchanged state if token parsing fails
    }

    // Use state.isSiriusEnabled instead of module variable
    if (state.isSiriusEnabled) {
      const currentUser = user
        ? { ...state.currentUser, ...user } // Merge for shallow immutability
        : state.currentUser; // Keep the same reference if no user provided

      const newToken = token || state.token; // Only update if new token exists
      const newHijacked = jwt.plru === undefined ? false : jwt.plru !== jwt.sub;

      // Early return if nothing has changed to prevent state mutation
      if (
        currentUser === state.currentUser &&
        newToken === state.token &&
        newHijacked === state.hijacked
      ) {
        return state;
      }

      return {
        ...state,
        authState: 'success',
        // Only update user if provided, otherwise keep existing
        currentUser,
        // Only update token if provided, otherwise keep existing
        token: newToken,
        // Only update hijacked status if we have a new JWT (plru is only from auth);
        hijacked: newHijacked,
      };
    }
    return {
      ...state,
      authState: 'success',
      currentUser: user,
      token,
      hijacked: jwt.plru && jwt.plru !== jwt.sub,
    };
  }),
  on(
    PLAuthError,
    (state): PLAuthState => ({
      ...state,
      authState: 'error',
      currentUser: null,
      token: null,
    })
  ),
  on(
    PLImpersonationStart,
    (state, { token, userData, targetUserId, expiresAt }): PLAuthState => ({
      ...state,
      impersonation: {
        isActive: true,
        token,
        userData,
        targetUserId,
        expiresAt,
      },
    })
  ),
  on(
    PLImpersonationStop,
    (state): PLAuthState => ({
      ...state,
      impersonation: {
        isActive: false,
        token: null,
        userData: null,
        targetUserId: null,
        expiresAt: null,
      },
    })
  )
);
